/** @jsx isolateComponent */
import React, { useRef, useState } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Animation,
  Colors,
  Heading,
  Spacer,
  Surface,
  Typography,
} from '@zapier/design-system';

import { SizeType } from '../../../types';

type CollapsedBlockProps = {
  /** Optional footer */
  footer?: React.ReactNode;
  /** Reduces the opacity of the component, except for the edit-link */
  isDimmed?: boolean;
  /** Called when the user clicks on the edit-link */
  onClickEdit: () => void;
  /** Title */
  title: string;
  /** Children to render */
  children: React.ReactNode;
  /** Size to display. */
  size?: SizeType;
};

const bodyCss = (size: SizeType) => css`
  padding: ${size === 'small' ? '20px 10px' : '30px 40px'};
  display: grid;
  grid-row-gap: 10px;
`;
const headingCss = (isDimmed: boolean, size: SizeType) => css`
  ${Typography[size === 'small' ? 'SectionHeader' : 'PageHeader']}
  transition-property: color;
  transition-timing-function: ${Animation.transitionTimingFunction};
  transition-duration: ${Animation.transitionDuration};
  color: ${Colors[isDimmed ? 'GrayWarm6' : 'PrimeBlack']};
`;
const contentCss = () => css`
  background-color: ${Colors.GrayWarm2};
  border-radius: 5px;
  padding: 0 15px;
  height: 50px;
  display: grid;
  grid-column-gap: 15px;
  grid-template-columns: 1fr min-content;
  align-items: center;
`;
const childrenCss = css`
  ${Typography.SmallPrint3}
  color: ${Colors.PrimeBlack};
  transition-property: opacity;
  transition-timing-function: ${Animation.transitionTimingFunction};
  transition-duration: ${Animation.transitionDuration};
  opacity: ${0.6};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const editCss = css`
  color: ${Colors.UiPrimaryStronger};
  text-decoration: underline;
  &:hover,
  &:focus {
    color: ${Colors.UiPrimaryStrongest};
  }
`;
const footerCss = css`
  border-radius: 0 0 10px 10px;
  overflow: hidden;
`;

/**
 * Renders a collapsed question.
 */
const CollapsedBlock = ({
  children,
  footer,
  isDimmed = false,
  onClickEdit,
  size = 'large',
  title,
}: CollapsedBlockProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const onMouseEnter = useRef(() => setIsHovered(true)).current;
  const onMouseLeave = useRef(() => setIsHovered(false)).current;

  const isHeadingDimmed = isDimmed && !isHovered;
  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm4">
        <div css={bodyCss(size)}>
          <Heading css={headingCss(isHeadingDimmed, size)}>{title}</Heading>
          <Spacer height={10} />
          <div css={contentCss()}>
            <div css={childrenCss}>{children}</div>
            <button css={editCss} onClick={() => onClickEdit()}>
              Edit
            </button>
          </div>
        </div>
        {footer && <div css={footerCss}>{footer}</div>}
      </Surface>
    </div>
  );
};

export default CollapsedBlock;
